import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { analyticsRepository } from '../repositories/analytics.repository';
import { analyticsKeys } from '../services/analytics.keys';
import type { ShareLink } from '../types/sharedAnalytics.types';

export type { ShareLink };

export const useCampaignShareLink = (campaignId: string) => {
  return useQuery<ShareLink | null, Error>({
    queryKey: analyticsKeys.shareLink(campaignId),
    queryFn: () => analyticsRepository.getCampaignShareLink(campaignId),
    enabled: !!campaignId,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateShareLink = () => {
  const queryClient = useQueryClient();

  return useMutation<ShareLink, Error, string>({
    mutationFn: (campaignId: string) => analyticsRepository.createShareLink(campaignId),
    onSuccess: (shareLink, campaignId) => {
      queryClient.setQueryData(analyticsKeys.shareLink(campaignId), shareLink);
      toast({
        title: 'Share link ready',
        description: 'Anyone with this link can view the public campaign report.',
      });
    },
    onError: (error: Error) => {
      console.error('Failed to create share link:', error);
      toast({
        title: 'Error creating share link',
        description: error.message || 'Please try again.',
        variant: 'destructive',
      });
    },
  });
};

export const useRevokeShareLink = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { token: string; campaignId: string }>({
    mutationFn: ({ token }) => analyticsRepository.revokeShareLink(token),
    onSuccess: (_revoked, variables) => {
      queryClient.setQueryData(analyticsKeys.shareLink(variables.campaignId), null);
      queryClient.invalidateQueries({ queryKey: analyticsKeys.shareLink(variables.campaignId) });
      toast({
        title: 'Share link revoked',
        description: 'The public analytics link is no longer accessible.',
      });
    },
    onError: (error: Error) => {
      console.error('Error revoking share link:', error);
      toast({
        title: 'Could not revoke share link',
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
    },
  });
};

